// AUTHORITATIVE importedTradesStorage (FIXED UNIQUE KEYS)
// Ensures every imported trade row is stored uniquely.
// NO collapsing by symbol.
// NO accidental dedupe across trades.

import { buildTenantStorageKey } from '../lib/storage/tenantStorage';
import { migrateOwnedLegacyPrivateValue, privateTradingStorageKey, readPrivateTradingValue, removePrivateTradingValue, writePrivateTradingValue } from '../lib/storage/privateTradingStorage';
import { normalizeTradeAssetClassification } from '../lib/trades/canonicalAssetClass';
import { normalizeVisibleTradeIds } from '../lib/trades/visibleTradeIds';

const IMPORTED_TRADES_STORAGE_KEY_BASE = 'trading.importedTrades.v2';
const IMPORTED_TRADES_STORAGE_KEY_LEGACY = 'tt_imported_trades';
// Logical event key retained for existing listeners; the actual browser key is
// computed only after the verified user+tenant scope is active.
export const IMPORTED_TRADES_STORAGE_KEY = 'private:imported-trades';
export const IMPORTED_TRADES_UPDATED_EVENT = 'imported-trades-updated';

import { normalizeTimestampToMs } from '../lib/time/normalizeTimestampToMs';

const STORAGE_DOMAIN = 'imported-trades';
const LEGACY_STORAGE_KEYS = [buildTenantStorageKey(IMPORTED_TRADES_STORAGE_KEY_BASE), IMPORTED_TRADES_STORAGE_KEY_BASE, IMPORTED_TRADES_STORAGE_KEY_LEGACY];

export function getImportedTradesStorageKey(): string | null {
  return privateTradingStorageKey(STORAGE_DOMAIN);
}

export type ImportedTradeRecord = any;

// Always guarantee unique key per record
function getKey(r: any, index: number): string {
  if (r.storageId) return String(r.storageId);
  if (r.originalId) return String(r.originalId) + ':' + index;
  return `row-${index}-${Date.now()}`;
}

export function clearImportedTrades() {
  removePrivateTradingValue(STORAGE_DOMAIN);
}

// Parse human-friendly duration strings (e.g. "1h 10m", "45m", "2d 3h")
export function parseDurationToMinutes(v: any): number | undefined {
  if (v == null) return undefined;
  if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
  if (typeof v === 'string') {
    const s = v.trim();
    const m = s.match(/(?:(\d+)\s*d)?\s*(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?/i);
    if (m) {
      const days = Number(m[1] || 0);
      const hours = Number(m[2] || 0);
      const mins = Number(m[3] || 0);
      const total = days * 1440 + hours * 60 + mins;
      if (Number.isFinite(total) && total >= 0) return total;
    }
    const num = Number(s);
    if (Number.isFinite(num)) return Math.round(num);
  }
  return undefined;
}

export function saveImportedTrades(records: any[], options: { source?: string } = {}): boolean {
  const unique: any[] = [];

  (records || []).forEach((r, i) => {
    const key = getKey(r, i);
    const normalizedTags = Array.isArray(r?.tags)
      ? r.tags
      : (typeof r?.tags === 'string'
        ? String(r.tags).split(/[;,|]/).map((x: string) => x.trim()).filter(Boolean)
        : []);

    // use top-level parseDurationToMinutes

    const fromField = parseDurationToMinutes(r.duration ?? r.durationMinutes);
    let normalizedDuration: number | undefined = fromField;

    if (normalizedDuration == null) {
      const entry = normalizeTimestampToMs(r?.entryTime ?? r?.entryTimestamp ?? r?.executedAt ?? r?.timestamp);
      const exit = normalizeTimestampToMs(r?.exitTime ?? r?.exitTimestamp ?? r?.closeTime ?? r?.exit_at);
      if (entry != null && exit != null && exit >= entry) {
        normalizedDuration = Math.max(0, Math.round((exit - entry) / 60000));
      }
    }

    unique.push(normalizeTradeAssetClassification({
      ...r,
      storageId: key,
      tags: normalizedTags,
      // canonical numeric minute value used by UI (both fields populated for compatibility)
      duration: normalizedDuration,
      durationMinutes: normalizedDuration ?? (typeof r.durationMinutes === 'number' ? Math.round(r.durationMinutes) : undefined),
    }));
  });

  // Gapless visible-ID normalization over the COMPLETE persisted collection.
  // The visible Trade ID is a compact display ordinal (T1..Tn), so every collection
  // change re-numbers it deterministically. Internal IDs and source trade IDs are
  // untouched, and re-running normalization reports zero changes.
  const normalized = normalizeVisibleTradeIds(unique);
  if (normalized.changed > 0) {
    console.warn(`[importedTradesStorage] Normalized ${normalized.changed} visible Trade ID(s) at persistence.`);
  }

  if (!writePrivateTradingValue(STORAGE_DOMAIN, normalized.records)) return false;

  try {
    window.dispatchEvent(
      new CustomEvent(IMPORTED_TRADES_UPDATED_EVENT, {
        detail: { total: unique.length, ...(options.source ? { source: options.source } : {}) }
      })
    );
  } catch {}
  return true;
}

export type ImportedTradesClearStorageState = 'available' | 'unavailable' | 'corrupt';

export function inspectImportedTradesForClear(): { state: ImportedTradesClearStorageState; records: any[] } {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined' || !getImportedTradesStorageKey()) return { state: 'unavailable', records: [] };
  const scoped = readPrivateTradingValue<unknown>(STORAGE_DOMAIN);
  if (scoped == null) return { state: 'available', records: [] };
  if (!Array.isArray(scoped) || scoped.some((record) => !record || typeof record !== 'object' || Array.isArray(record))) return { state: 'corrupt', records: [] };
  return { state: 'available', records: scoped };
}

export function isCsvImportedTradeForClear(trade: any): boolean {
  const source = String(trade?.sourceType ?? trade?.source_type ?? trade?.source ?? '').trim().toLowerCase();
  return source === 'csv' || source === 'csv_import' || source === 'local_import';
}

export function normalizeClearBroker(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export function getClearAccountIdentity(trade: any): { broker: string; ref: string; name: string | null; sourceAccountId: string | null } | null {
  const broker = normalizeClearBroker(trade?.sourceBroker ?? trade?.source_broker ?? trade?.broker ?? trade?.brokerId);
  const explicitRef = String(trade?.sourceAccountRef ?? trade?.source_account_ref ?? '').trim();
  const accountValue = String(trade?.account ?? '').trim();
  const ref = explicitRef || (accountValue.startsWith('ibkr_csv:') ? accountValue : '');
  if (!broker || broker === 'csv_generic' || !ref) return null;
  const name = String(trade?.accountName ?? trade?.account_name ?? trade?.accountTag ?? trade?.account_tag ?? accountValue).trim().replace(/\s+/g, ' ');
  const sourceAccountId = String(trade?.sourceAccountId ?? trade?.source_account_id ?? '').trim();
  return { broker, ref, name: name || null, sourceAccountId: sourceAccountId || null };
}

export function hasUserAuthoredTradeData(trade: any): boolean {
  const fields = ['notes', 'note', 'journalEntry', 'journal_entry', 'strategy', 'review', 'screenshots', 'attachments'];
  if (fields.some((field) => {
    const value = trade?.[field];
    return Array.isArray(value) ? value.length > 0 : String(value ?? '').trim().length > 0;
  })) return true;
  return Array.isArray(trade?.tags) ? trade.tags.length > 0 : Boolean(String(trade?.tags ?? '').trim());
}

export function writeImportedTradesForClear(records: any[]): boolean {
  return writePrivateTradingValue(STORAGE_DOMAIN, Array.isArray(records) ? records : []);
}

export function removeImportedTradesForAccount(args: {
  sourceBroker: string;
  sourceAccountRef: string;
  sourceAccountId: string;
  /** Only allowed after a fresh complete server read verifies this account is empty. */
  allowVerifiedReferenceMatch?: boolean;
  emitEvent?: boolean;
}): { removed: any[]; remaining: any[]; unresolvedCount: number; ok: boolean } {
  const inspected = inspectImportedTradesForClear();
  if (inspected.state !== 'available') return { removed: [], remaining: inspected.records, unresolvedCount: 0, ok: false };
  const broker = normalizeClearBroker(args.sourceBroker);
  const targetRef = String(args.sourceAccountRef || '').trim();
  const targetId = String(args.sourceAccountId || '').trim();
  if (!broker || !targetRef || !targetId) return { removed: [], remaining: inspected.records, unresolvedCount: 0, ok: false };

  const unresolvedCount = inspected.records.filter((trade) => {
    if (!isCsvImportedTradeForClear(trade)) return false;
    const identity = getClearAccountIdentity(trade);
    return identity?.broker === broker && identity.ref === targetRef && !identity.sourceAccountId;
  }).length;
  const removed: any[] = [];
  const remaining: any[] = [];
  for (const trade of inspected.records) {
    const identity = getClearAccountIdentity(trade);
    const matches = isCsvImportedTradeForClear(trade)
      && identity?.broker === broker
      && identity.ref === targetRef
      && (identity.sourceAccountId === targetId || (args.allowVerifiedReferenceMatch === true && !identity.sourceAccountId));
    (matches ? removed : remaining).push(trade);
  }
  if (unresolvedCount > 0 && args.allowVerifiedReferenceMatch !== true) return { removed: [], remaining: inspected.records, unresolvedCount, ok: false };
  const ok = writeImportedTradesForClear(remaining);
  if (ok && args.emitEvent !== false) {
    try { window.dispatchEvent(new CustomEvent(IMPORTED_TRADES_UPDATED_EVENT, { detail: { total: remaining.length } })); } catch {}
  }
  return { removed: ok ? removed : [], remaining: ok ? remaining : inspected.records, unresolvedCount, ok };
}

export function isImportedTradeForAccount(trade: any, args: {
  sourceBroker: string;
  sourceAccountRef: string;
  sourceAccountId: string;
}): boolean {
  const identity = getClearAccountIdentity(trade);
  return isCsvImportedTradeForClear(trade)
    && identity?.broker === normalizeClearBroker(args.sourceBroker)
    && identity.ref === String(args.sourceAccountRef || '').trim()
    && identity.sourceAccountId === String(args.sourceAccountId || '').trim();
}

export function countImportedTradesForSavedAccount(records: any[], sourceAccountId: string | null | undefined): number {
  const accountId = String(sourceAccountId || '').trim();
  if (!accountId) return 0;
  return (Array.isArray(records) ? records : []).filter((trade) =>
    isCsvImportedTradeForClear(trade)
    && String(trade?.sourceAccountId ?? trade?.source_account_id ?? '').trim() === accountId
  ).length;
}

export function getSavedAccountImportedTradeCount(account: { id?: string; tradeCount?: number | null } | null | undefined, records: any[]): number {
  if (!account?.id) return 0;
  const serverCount = Number(account.tradeCount);
  return Number.isFinite(serverCount) && serverCount >= 0
    ? serverCount
    : countImportedTradesForSavedAccount(records, account.id);
}

export function isCsvImportedTradeForSavedAccount(trade: any, account: { id?: string; sourceAccountRef?: string; sourceBroker?: string } | null | undefined): boolean {
  const accountId = String(account?.id || '').trim();
  if (!accountId || !isCsvImportedTradeForClear(trade)) return false;
  const tradeAccountId = String(trade?.sourceAccountId ?? trade?.source_account_id ?? '').trim();
  if (tradeAccountId) return tradeAccountId === accountId;
  const tradeRef = String(trade?.sourceAccountRef ?? trade?.source_account_ref ?? '').trim();
  const tradeBroker = normalizeClearBroker(trade?.sourceBroker ?? trade?.source_broker ?? trade?.broker ?? trade?.brokerId);
  return Boolean(tradeRef && account?.sourceAccountRef && tradeRef === account.sourceAccountRef
    && tradeBroker && tradeBroker === normalizeClearBroker(account.sourceBroker));
}

export function filterTradesForSelectedCsvAccount<T extends Record<string, any>>(trades: T[], account: { id?: string; sourceAccountRef?: string; sourceBroker?: string } | null | undefined): T[] {
  const accountId = String(account?.id || '').trim();
  if (!accountId) return Array.isArray(trades) ? trades : [];
  return (Array.isArray(trades) ? trades : []).filter((trade) =>
    !isCsvImportedTradeForClear(trade) || isCsvImportedTradeForSavedAccount(trade, account)
  );
}

export function filterTradesForSelectedTradingAccount<T extends Record<string, any>>(trades: T[], accountId: string | null | undefined): T[] {
  const selectedAccountId = String(accountId || '').trim();
  if (!selectedAccountId) return Array.isArray(trades) ? trades : [];
  return (Array.isArray(trades) ? trades : []).filter((trade) =>
    String(trade?.sourceAccountId ?? trade?.source_account_id ?? '').trim() === selectedAccountId
  );
}

export function restoreMissingImportedTrades(recordsToRestore: any[], currentRecords: any[]): boolean {
  const byStorageId = new Map<string, any>();
  for (const record of [...(Array.isArray(currentRecords) ? currentRecords : []), ...(Array.isArray(recordsToRestore) ? recordsToRestore : [])]) {
    const id = String(record?.storageId ?? record?.storage_id ?? '').trim();
    if (!id) return false;
    if (!byStorageId.has(id)) byStorageId.set(id, record);
  }
  return writeImportedTradesForClear([...byStorageId.values()]);
}

export function loadImportedTrades(): any[] {
  try {
    if (!getImportedTradesStorageKey()) return [];
    const parsed = readPrivateTradingValue<unknown>(STORAGE_DOMAIN)
      ?? migrateOwnedLegacyPrivateValue<unknown>(STORAGE_DOMAIN, LEGACY_STORAGE_KEYS);
    if (!Array.isArray(parsed)) return [];

    // Normalize legacy/older records so UI can rely on numeric `duration` / `durationMinutes`.
    const normalized = parsed.map((r: any) => {
      const out = { ...r };
      if (out.duration == null) {
        const byField = parseDurationToMinutes(out.duration ?? out.durationMinutes);
        if (byField != null) {
          out.duration = byField;
          out.durationMinutes = byField;
        } else {
          const entry = normalizeTimestampToMs(out?.entryTime ?? out?.entryTimestamp ?? out?.executedAt ?? out?.timestamp);
          const exit = normalizeTimestampToMs(out?.exitTime ?? out?.exitTimestamp ?? out?.closeTime ?? out?.exit_at);
          if (entry != null && exit != null && exit >= entry) {
            const computed = Math.max(0, Math.round((exit - entry) / 60000));
            out.duration = computed;
            out.durationMinutes = computed;
          }
        }
      }
      return normalizeTradeAssetClassification(out);
    });

    // Gapless visible-ID normalization over the COMPLETE persisted collection
    // (deterministic, idempotent, preserves internal IDs, source IDs, and all trade
    // data). Persist the corrected IDs so the rendered table reads the persisted
    // visible value directly.
    const normalizedOutcome = normalizeVisibleTradeIds(normalized);
    if (normalizedOutcome.changed > 0) {
      console.warn(`[importedTradesStorage] Normalized ${normalizedOutcome.changed} visible Trade ID(s) on load.`);
      try { writePrivateTradingValue(STORAGE_DOMAIN, normalizedOutcome.records); } catch {}
    }
    return normalizedOutcome.records;
  } catch {
    return [];
  }
}
